import React, { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useDataProvider, useNotify } from 'react-admin';
import { formatDateTime } from '../../components/ex_dayjs';
import { renderDropCell } from './InventoryDataGrid';
import MDialog from '../../components/controls/MDialog';

/** Cell style for right-aligned summary cells, mirroring the DatagridTotals pattern. */
const cellStyle: React.CSSProperties = {
    padding: '12px 24px',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
};

/** Event record shape returned by `EventLevel/GetEvents`. */
interface EventRecord {
    siteId?: string;
    eventName?: string;
    eventDate?: string;
    /** GA or Reserved classification computed server-side. */
    type?: string;
    hAgoCount?: number;
    currentCount?: number;
    prev48hCount?: number;
    prev72hCount?: number;
    oneWeekAgoCount?: number;
    twoWeeksAgoCount?: number;
    monthAgoCount?: number;
    [key: string]: unknown;
}

/** Venue record used to identify which events to load. */
interface VenueRecord {
    venueName?: string;
    marketType?: string;
    [key: string]: unknown;
}

/** Props for the {@link SummaryRow} component. */
interface SummaryRowProps {
    /** Event records whose numeric fields are summed into totals. */
    events: EventRecord[];
    /** Drop-window size in hours, used for the hAgo column. */
    hours: number;
}

/**
 * Running-totals bar rendered below the event-level DataGrid.
 * Mirrors the `DatagridTotals` pattern from {@link InventoryDataGrid}:
 * measures the rendered column-header widths and aligns each total cell
 * to its corresponding column using CSS grid.
 *
 * Column order must match the {@link columnDefs} array defined in
 * {@link EventDetailsPopup}:
 * id | eventName | eventDate | type | hAgoCount | 48h | 72h | 7d | 14d | 1m
 *
 * @param props - {@link SummaryRowProps}
 */
const SummaryRow: React.FC<SummaryRowProps> = ({ events, hours: _hours }) => {
    const ref = useRef<HTMLDivElement>(null);

    const sum = (key: keyof EventRecord) =>
        events.reduce((s, e) => s + ((e[key] as number) || 0), 0);

    const totals = {
        latestRecord: sum('latestRecord'),
        current:      sum('currentCount'),
        hAgo:         sum('hAgoCount'),
        _48h:         sum('prev48hCount'),
        _72h:         sum('prev72hCount'),
        d7:           sum('oneWeekAgoCount'),
        d14:          sum('twoWeeksAgoCount'),
        m1:           sum('monthAgoCount'),
    };

    useEffect(() => {
        const headerCells = document.querySelectorAll<HTMLElement>('.MuiDataGrid-columnHeader');
        if (!headerCells.length || !ref.current) return;
        const cols = Array.from(headerCells).map((c) => `${c.offsetWidth}px`).join(' ');
        ref.current.style.gridTemplateColumns = cols;
    }, [events]);

    if (!events.length) return null;

    return (
        <Box
            ref={ref}
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(10, auto)', // fallback
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                alignItems: 'center',
            }}
        >
            {/* id column — "Total" label */}
            <span style={{ padding: '6px 16px' }}><b>Total</b></span>
            {/* eventName — no aggregate */}
            <span />
            {/* eventDate — no aggregate */}
            <span/>
            {/* type — no aggregate */}
            <span/>
            <span style={cellStyle}>{totals.latestRecord.toLocaleString()}</span>
            {/* hAgoCount — drop indicator comparing current vs hAgo baseline */}
            <span style={{ paddingRight: 24 }}>{renderDropCell(totals.current, totals.hAgo)}</span>
            <span style={cellStyle}>{totals._48h.toLocaleString()}</span>
            <span style={cellStyle}>{totals._72h.toLocaleString()}</span>
            <span style={cellStyle}>{totals.d7.toLocaleString()}</span>
            <span style={cellStyle}>{totals.d14.toLocaleString()}</span>
            <span style={cellStyle}>{totals.m1.toLocaleString()}</span>
        </Box>
    );
};

/** Props for {@link EventDetailsPopup}. */
interface EventDetailsPopupProps {
    /** Whether the dialog is currently open. */
    open: boolean;
    /** Callback invoked when the user dismisses the dialog. */
    onClose: () => void;
    /** The venue whose events to display, or `null` when no venue is selected. */
    venue: VenueRecord | null;
    /** Drop-window size in hours used for column headers and summary calculations. */
    hours?: number;
}

/**
 * Modal dialog that lazily loads and displays per-event inventory statistics
 * for a selected venue.  Fetches data from `EventLevel/GetEvents`
 * whenever `open` and `venue` change.
 *
 * @param props - {@link EventDetailsPopupProps}
 */
const EventDetailsPopup: React.FC<EventDetailsPopupProps> = ({
    open,
    onClose,
    venue,
    hours = 48,
}) => {
    const dataProvider = useDataProvider();
    const notify       = useNotify();
    const [events, setEvents]       = useState<EventRecord[]>([]);
    const [loading, setLoading]     = useState<boolean>(false);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    useEffect(() => {
        if (!open || !venue) return;
        setLoading(true);
        setEvents([]);
        const params = new URLSearchParams({
            venue:      venue.venueName || '',
            marketType: venue.marketType || '',
            hours:      String(hours),
        });
        dataProvider
            .fetchData('EventLevel', `GetEvents?${params}`)
            .then((response: { data?: EventRecord[] }) => {
                setEvents(Array.isArray(response?.data) ? response.data : []);
                setSortModel([{ field: 'latestRecord', sort: 'desc' }]);
            })
            .catch((err: Error) => {
                notify(`Failed to load events: ${err?.message}`, { type: 'error', autoHideDuration: 4000 });
            })
            .finally(() => setLoading(false));
    }, [open, venue]);

    if (!venue) return null;

    const customFormatDate = (str: string) => {
        const [date, time, ampm] = str.split(' ');
        return `${date}\n${time} ${ampm}`;
    };

    /**
     * Column definitions — order must stay in sync with {@link SummaryRow} cell order:
     * id | eventName | eventDate | type | hAgoCount | 48h | 72h | 7d | 14d | 1m
     */
    const columnDefs: GridColDef[] = [
        { field: 'id', headerName: 'Site ID', width: 100, flex: 0.5 },
        {
            field: 'eventName',
            headerName: 'Event Name',
            width: 300,
            flex: 1,
            renderCell: (params) => (
                <a href={params.row.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                    {params.row.eventName}
                </a>
            ),
        },
        {
            field: 'eventDate',
            headerName: 'Event Date',
            width: 180,
            valueFormatter: (value: string) => value ? customFormatDate(formatDateTime(value) || '') : '-',
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 100,
        },
        {
            field: 'latestRecord',
            headerName: 'Latest',
            width: 150,
            align: 'right',
            headerAlign: 'right',
            renderHeader: () => (
                <Tooltip title="Latest(24h/48h/72h/7d)">
                    <span>Latest</span>
                </Tooltip>
            ),
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
        {
            field: 'hAgoCount',
            headerName: `${hours}h Ago`,
            width: 130,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => renderDropCell(params.row.currentCount, params.row.hAgoCount),
        },
        {
            field: 'prev48hCount',
            headerName: '48h Ago',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
        {
            field: 'prev72hCount',
            headerName: '72h Ago',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
        {
            field: 'oneWeekAgoCount',
            headerName: '7 Days Ago',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
        {
            field: 'twoWeeksAgoCount',
            headerName: '14 Days Ago',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
        {
            field: 'monthAgoCount',
            headerName: '1 Month Ago',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value: number) => value?.toLocaleString() || '-',
        },
    ];

    return (
        <MDialog
            fullWidth
            open={open}
            title={`Market: ${venue.marketType} | ${events.length} events dropped in multiple drop windows`}
            handleClose={onClose}
            maxWidth="xl"
            msgLoading={loading ? 'Loading events...' : undefined}
        >
            <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                    rows={events}
                    columns={columnDefs}
                    getRowId={(row: EventRecord) => row.siteId || Math.random()}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    hideFooterPagination={true}
                    hideFooter={true}
                    density="compact"
                    disableRowSelectionOnClick
                />
            </Box>
            {/* Summary totals aligned with the DataGrid columns */}
            <SummaryRow events={events} hours={hours} />
        </MDialog>
    );
};

export default EventDetailsPopup;
